import {
  CheckCircle2,
  ChevronRight,
  Layers3,
  Loader2,
  XCircle,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type {
  TeamAiSkill,
  TeamAiSkillEvaluationRun,
} from './admin-team-ai-skills.types';
import {
  formatSkillDate,
  skillStateBadgeVariant,
} from './admin-team-ai-skills.utils';

export function SkillCatalogList({
  skills,
  selectedSkillId,
  onSelect,
}: {
  skills: TeamAiSkill[];
  selectedSkillId: string | null;
  onSelect: (skillId: string) => void;
}) {
  if (skills.length === 0) {
    return (
      <div className="flex min-h-52 flex-col items-center justify-center border-b px-5 text-center lg:border-b-0 lg:border-r">
        <Layers3 className="mb-3 h-8 w-8 text-muted-foreground" />
        <p className="text-sm font-medium">No Team AI skills yet</p>
        <p className="mt-1 max-w-56 text-xs text-muted-foreground">
          Create or import a PM or Coding AI skill to start the controlled
          evaluation lifecycle.
        </p>
      </div>
    );
  }

  return (
    <div className="max-h-[70vh] overflow-y-auto border-b lg:border-b-0 lg:border-r">
      {skills.map((skill) => {
        const latestVersion = skill.versions[0];
        const selected = skill.id === selectedSkillId;
        return (
          <button
            type="button"
            key={skill.id}
            onClick={() => onSelect(skill.id)}
            className={cn(
              'flex w-full items-start gap-3 border-b px-4 py-4 text-left transition-colors last:border-b-0 hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary',
              selected && 'bg-primary/10',
            )}
          >
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="truncate text-sm font-medium">
                  {skill.name}
                </span>
                <Badge variant="outline">{skill.category}</Badge>
                <Badge variant="secondary">{skill.agent}</Badge>
              </div>
              <p className="mt-1 truncate font-mono text-xs text-muted-foreground">
                {skill.key}
              </p>
              <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                <span>{skill.versions.length} version(s)</span>
                {latestVersion && (
                  <Badge variant={skillStateBadgeVariant(latestVersion.state)}>
                    v{latestVersion.version} {latestVersion.state}
                  </Badge>
                )}
              </div>
            </div>
            <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
          </button>
        );
      })}
    </div>
  );
}

export function EvaluationEvidence({
  runs,
  loading,
}: {
  runs: TeamAiSkillEvaluationRun[];
  loading: boolean;
}) {
  const [showAll, setShowAll] = useState(false);
  useEffect(() => setShowAll(false), [runs]);
  if (loading) return <Skeleton className="h-32 w-full" />;
  if (runs.length === 0) {
    return (
      <div className="border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
        No evaluation evidence is stored for this version.
      </div>
    );
  }

  const visibleRuns = showAll ? runs : runs.slice(0, 5);
  return (
    <div className="space-y-3">
      {visibleRuns.map((run) => (
        <details key={run.id} className="group border bg-background">
          <summary className="flex cursor-pointer list-none flex-wrap items-center gap-3 px-4 py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary">
            {run.state === 'passed' ? (
              <CheckCircle2 className="h-4 w-4 text-success" />
            ) : run.state === 'failed' ? (
              <XCircle className="h-4 w-4 text-destructive" />
            ) : (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
            <Badge
              variant={
                run.state === 'passed'
                  ? 'success'
                  : run.state === 'failed'
                    ? 'destructive'
                    : 'running'
              }
            >
              {run.state}
            </Badge>
            <span className="text-sm font-medium">
              {run.passedCases}/{run.totalCases} cases passed
            </span>
            <span className="ml-auto text-xs text-muted-foreground">
              Suite v{run.suiteVersion} · {formatSkillDate(run.startedAt)}
            </span>
          </summary>
          <div className="border-t px-4 py-4">
            <div className="mb-4 grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
              <p>
                Requested alias:{' '}
                <code className="font-mono">{run.requestedModel}</code>
              </p>
              <p className="sm:text-right">
                Content:{' '}
                <code className="font-mono">
                  {run.contentHash.slice(0, 12)}
                </code>
              </p>
            </div>
            {run.failureReason && (
              <p className="mb-4 text-sm text-destructive">
                {run.failureReason}
              </p>
            )}
            <div className="overflow-x-auto border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Case</TableHead>
                    <TableHead>Expected</TableHead>
                    <TableHead>Actual</TableHead>
                    <TableHead>Model</TableHead>
                    <TableHead className="text-right">Duration</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {run.cases.map((testCase) => (
                    <TableRow key={testCase.id}>
                      <TableCell className="min-w-56 align-top">
                        <div className="flex items-start gap-2">
                          {testCase.passed ? (
                            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                          ) : (
                            <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                          )}
                          <div>
                            <p className="font-medium">{testCase.caseId}</p>
                            <p className="text-xs text-muted-foreground">
                              {testCase.dimension}
                            </p>
                            {testCase.failureReason && (
                              <p className="mt-1 text-xs text-destructive">
                                {testCase.failureReason}
                              </p>
                            )}
                            {testCase.output && (
                              <details className="mt-2">
                                <summary className="cursor-pointer text-xs text-primary">
                                  Inspect bounded output
                                </summary>
                                <pre className="mt-2 max-h-48 max-w-lg overflow-auto whitespace-pre-wrap bg-muted p-3 font-mono text-xs text-foreground">
                                  {testCase.output}
                                </pre>
                              </details>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="align-top text-xs">
                        <p>{testCase.expectedAction}</p>
                        {testCase.expectedRecipeId && (
                          <code className="font-mono text-muted-foreground">
                            {testCase.expectedRecipeId}
                          </code>
                        )}
                      </TableCell>
                      <TableCell className="align-top text-xs">
                        <p>{testCase.actualAction || 'No valid action'}</p>
                        {testCase.actualRecipeId && (
                          <code className="font-mono text-muted-foreground">
                            {testCase.actualRecipeId}
                          </code>
                        )}
                      </TableCell>
                      <TableCell className="max-w-44 truncate align-top text-xs">
                        {testCase.actualModel || 'No response'}
                      </TableCell>
                      <TableCell className="text-right align-top text-xs">
                        {testCase.durationMs} ms
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </details>
      ))}
      {runs.length > 5 && (
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={() => setShowAll((current) => !current)}
        >
          {showAll ? 'Show less' : `Show ${runs.length - 5} more runs`}
        </Button>
      )}
    </div>
  );
}
